import "core-js/modules/es.string.includes";
import "core-js/modules/es.string.split";
import "core-js/modules/web.dom-collections.iterator";
import "core-js/modules/es.string.includes";
import "core-js/modules/es.string.split";
import "core-js/modules/web.dom-collections.iterator";
/**
 * Section: Site header
 * ------------------------------------------------------------------------------
 * Contains highly coupled scripts to control site header components.
 *
 * @namespace siteHeader
 */

import { register } from '@shopify/theme-sections';
import Cookies from 'js-cookie';
import Modal from '~/components/modal';
import Notification from '~/components/notification';
import Toggle from '~/components/toggle';
import cssClasses from '~/helpers/cssClasses';
import { on, getSiblings } from '~/helpers/utils';
import ScrollDirection from '../mixins/scroll-direction';
/**
 * DOM selectors.
 */

const selectors = {
  container: '[js-site-header="container"]',
  currencySelector: '[js-currency-selector="container"]',
  currencySelectorDropdown: '[js-currency-selector="dropdown"]',
  currencySelectorOption: '[js-currency-selector="option"]',
  currencySelectorWrap: '[js-currency-selector="wrap"]',
  dropdownParent: '[js-site-dropdown="dropdownParent"]',
  megaNavToggle: '[js-site-header="megaNavToggle"]',
  navLink: '[js-site-header="navLink"]',
  navListItem: '.navlist__item',
  navListLink: '.navlist__link',
  redirectModalToggle: '[js-site-header="redirectModalToggle"]',
  searchBarHeader: '[js-site-header="searchBar"]',
  searchBarToggle: '[js-site-header="searchBarToggle"]',
  searchInput: '[js-site-header="input"]',
  shopNavMenuHandle: '[js-site-nav="shopNavMenuHandle"]',
  siteHeaderInner: '[js-site-header="siteHeaderInner"]',
  siteHeaderMasthead: '.site-header__masthead',
  siteHeaderWrapper: '[js-site-header="wrapper"]',
  siteNav: '.site-nav',
  siteNavDropDown: '[js-site-header="siteNavDropdown"]',
  siteNavItem: '[js-site-header="siteNavItem"]',
  siteNavLink: '[js-site-header="siteNavLink"]',
  storeIcon: '[js-site-header="storeIcon"]'
};
/**
 * Instance cookies.
 */

const cookieSelector = {
  geolocation: 'redirect_geolocation',
  settings: 'redirect_store_settings'
};
const hideMegaNavTimer = 500;
let blurTimer;
/**
 * Register the `site-header` section type.
 */

register('site-header', {
  /**
   * Callback function when the section is loaded in via the  `sections.load()`
   * function or by the Theme Editor `shopify:section:unload` event.
   */
  onLoad() {
    this.constructComponents();
    this.init();
  },

  /**
   * Instantiate a collapsed header on scroll using scrollDirection helper.
   */
  constructStickyHeader() {
    this.AnimatedHeader = ScrollDirection({
      throttle: 100,
      start: 0
    });
  },

  /**
   * Component selectors.
   */
  setNodeSelectors() {
    this.container = document.querySelector(selectors.container);
    this.currencySelector = document.querySelectorAll(selectors.currencySelector);
    this.currencySelectorDropdown = this.container.querySelector(selectors.currencySelectorDropdown);
    this.currencySelectorWrap = document.querySelector(selectors.currencySelectorWrap);
    this.dropdownParent = [...document.querySelectorAll(selectors.dropdownParent)];
    this.megaNavToggle = [...document.querySelectorAll(selectors.megaNavToggle)];
    this.navLink = [...document.querySelectorAll(selectors.navLink)];
    this.navListItem = [...document.querySelectorAll(selectors.navListItem)];
    this.navListLink = [...document.querySelectorAll(selectors.navListLink)];
    this.redirectModalToggle = document.querySelector(selectors.redirectModalToggle);
    this.searchBarHeader = document.querySelector(selectors.searchBarHeader);
    this.searchBarToggle = document.querySelectorAll(selectors.searchBarToggle);
    this.searchInput = document.querySelector(selectors.searchInput);
    this.shopNavMenuHandle = [...document.querySelectorAll(selectors.shopNavMenuHandle)];
    this.siteHeaderInner = document.querySelector(selectors.siteHeaderInner);
    this.siteHeaderMasthead = document.querySelector(selectors.siteHeaderMasthead);
    this.siteHeaderWrapper = document.querySelector(selectors.siteHeaderWrapper);
    this.siteNav = document.querySelector(selectors.siteNav);
    this.siteNavDropDown = [...document.querySelectorAll(selectors.siteNavDropDown)];
    this.storeIcon = document.querySelector(selectors.storeIcon);
  },

  /**
   * Component variables.
   */
  setVariables() {
    this.ariaExpandedString = 'aria-expanded';
    this.blurTimer = blurTimer;
    this.hideMegaNavTimer = hideMegaNavTimer;
  },

  /**
   * Re-paint body padding-top using current header height.
   */
  updateBodyOffset() {
    document.body.style.paddingTop = "".concat(this.container.parentNode.offsetHeight, "px");
  },

  /**
   * Instantiate search bar toggle.
   */
  constructSearchToggle() {
    this.SearchBar = Toggle({
      exclude: ['[data-target="searchBarHeader"]'],
      toggleSelector: 'searchBarHeader',
      overlay: false
    });
  },

  /**
   * Instantiate announcement component.
   */
  constructAnnouncement() {
    this.Announcement = Notification({
      container: '.site-header__announcement',
      enableCookies: true,
      cookie: {
        namespace: 'frame_announcement',
        value: 'dismissed'
      }
    });
  },

  /**
   * Construct section components.
   */
  constructComponents() {
    this.constructSearchToggle();
    this.constructAnnouncement();
    this.constructStickyHeader();
  },

  /**
   * Initialize component.
   */
  init() {
    this.setNodeSelectors();
    this.setEventListeners();
    this.setStickyHeader();
    this.setEventBusListeners();
    this.setSearchToggle();
    this.revealHeader();

    if (theme.features.redirectModal && this.redirectModalToggle) {
      this.setRedirectModalToggle();
      this.setStoreIcon();
    }
  },

  /**
   * Change header opacity to 1
   */
  revealHeader() {
    this.siteHeaderInner.classList.add(cssClasses.active);
  },

  /**
   * Set sticky header events after document has loaded styling.
   */
  setStickyHeader() {
    on('load', () => {
      this.AnimatedHeader.init();
    });
  },

  /**
   * Handle resize events by requesting animation frame.
   */
  handleResizeEvents() {
    this.updateBodyOffset();
  },

  /**
   * Set redirect modal toggle events.
   */
  setRedirectModalToggle() {
    const modal = Modal({
      id: 'redirect-modal'
    });

    if (!this.redirectModalToggle) {
      return;
    }

    on('click', this.redirectModalToggle, event => {
      event.preventDefault();
      modal.show('redirect-modal');
    });
  },

  /**
   * Set search toggle events.
   */
  setSearchToggle() {
    this.SearchBar.init();
  },

  /**
   * Get store icon using cookies and other stored information.
   * @returns {String}
   */
  getStoreIcon() {
    const storeSettings = Cookies.getJSON(cookieSelector.settings);
    let storeCode = typeof storeSettings === 'undefined' ? theme.store.storeCode : storeSettings.loc;

    if (window.location.href.includes('?loc=')) {
      if (window.location.href.split('?loc=')[1].substring(0, 3) === 'ROW') {
        storeCode = 'ROW';
      } else {
        storeCode = window.location.href.split('?loc=')[1].substring(0, 2);
      }
    }

    return storeCode.toLowerCase();
  },

  /**
   * Update the icon based on the user's store settings.
   * @param {String} countryCode - The country code.
   */
  setStoreIcon() {
    let countryCode = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : this.getStoreIcon();
    this.storeIcon.className = "flag-icon flag-icon-".concat(countryCode);
  },

  /**
   * Cancels the blur timer if the focus is still on a Nav Link item.
   * @param {HTMLElement} element - The Mega Nav link element
   */
  setKeyFocus(element) {
    on('focus', element, () => window.clearTimeout(this.blurTimer));
  },

  /**
   * Sets a timer that hides the Mega Nav if no Site Nav element is focused.
   * @param {HTMLElement} element - The Mega Nav link element.
   */
  setKeyBlur(element) {
    const siteNavParent = this.getSiteNavParent(element);
    on('blur', element, () => {
      this.blurTimer = window.setTimeout(() => this.hideMegaNav(siteNavParent), hideMegaNavTimer);
    });
  },

  /**
   * Hides active sibling Mega Nav items and sets aria-expanded to false using hideMegaNav function.
   * @param {HTMLElement} element - The Mega Nav toggle button.
   */
  hideSiblingMegaNavs(element) {
    const siteHeader = element.parentNode;
    siteHeader.childNodes.forEach(child => {
      if (child !== element && child.nodeType === 1 && child.classList.contains(cssClasses.active)) {
        this.hideMegaNav(child);
      }

      return element;
    });
  },

  /**
   * Displays a Mega Nav item and sets aria-expanded to true.
   * @param {HTMLElement} siteNavParent - The Mega Nav.
   */
  showMegaNav(siteNavParent) {
    siteNavParent.classList.add(cssClasses.active);
    siteNavParent.querySelector(selectors.siteNavLink).setAttribute(this.ariaExpandedString, true);
    siteNavParent.querySelector(selectors.megaNavToggle).setAttribute(this.ariaExpandedString, true);
  },

  /**
   * Hides a Mega Nav item and sets aria-expanded to false.
   * @param {HTMLElement} siteNavParent - The Mega Nav.
   */
  hideMegaNav(siteNavParent) {
    siteNavParent.classList.remove(cssClasses.active);
    siteNavParent.querySelector(selectors.siteNavLink).setAttribute(this.ariaExpandedString, false);
    siteNavParent.querySelector(selectors.megaNavToggle).setAttribute(this.ariaExpandedString, false);
  },

  /**
   * Selects the siteNav parent.
   * @param {HTMLElement} element - The Nav List item or the megaNavToggle button.
   * @returns {HTMLElement}
   */
  getSiteNavParent(element) {
    return element.closest(selectors.siteNavItem);
  },

  /**
   * Toggle Mega Nav, update aria tags and hide other Mega Nav items.
   * @param {HTMLElement} element - The Nav List item or the Mega Nav Toggle button.
   */
  toggleMegaNav(element) {
    const siteNavParent = this.getSiteNavParent(element);
    this.hideSiblingMegaNavs(siteNavParent);

    if (siteNavParent.classList.contains(cssClasses.active)) {
      this.hideMegaNav(siteNavParent);
      return element;
    }

    this.showMegaNav(siteNavParent);
    return element;
  },

  /**
   * reset the secondary menu link's hovering state
   *
   */
  resetMenuDropDownHoverState() {
    this.navListItem.forEach(item => {
      item.classList.remove(cssClasses.hovered);
      item.classList.remove(cssClasses.disabled);
    });
  },

  /**
  * Set event bus listeners based on scroll direction.
  */
  setEventListeners() {
    this.dropdownParent.forEach(element => {
      on('mouseenter', element, () => {
        if (!this.searchBarHeader.getAttribute('js-search-bar-active')) {
          return;
        }

        this.SearchBar.close(this.searchBarHeader);
      });
    });
    this.navLink.forEach(element => {
      const elementParent = element.parentNode;
      on('mouseenter', element, () => {
        this.highlightElement(elementParent);
      });
    });
    this.shopNavMenuHandle.forEach(element => {
      const elementParent = element.parentNode;
      const parentDropDown = elementParent.parentNode;
      const handle = element.getAttribute('data-nav-menu-handle');
      on('mouseenter', element, () => {
        if (!parentDropDown.classList.contains(cssClasses.expanded)) {
          parentDropDown.classList.add(cssClasses.expanded);
        }

        Frame.EventBus.emit('Toggle:shopNavigationMenu:close');
        Frame.EventBus.emit('Toggle:shopNavigationMenu:open', handle);
      });
      on('click', element, event => {
        event.preventDefault();
      });
    });
    this.siteNavDropDown.forEach(siteNavDropDown => {
      on('mouseleave', siteNavDropDown, event => {
        if (event.relatedTarget !== null) {
          return;
        }

        this.siteHeaderMasthead.classList.remove(cssClasses.hovered);
        Frame.EventBus.emit('Toggle:shopNavigationMenu:close');
        this.resetMenuDropDownHoverState();
        this.resetDropDownExpansion();
        this.resetDropDownState();
      });
    });
    on('mouseover', this.siteHeaderMasthead, event => {
      event.stopPropagation();
      event.preventDefault();

      if (event.target.classList.contains('site-header__inner') || event.target.classList.contains('site-header__misc') || event.target.classList.contains('site-nav__link')) {
        this.siteHeaderMasthead.classList.remove(cssClasses.hovered);
        Frame.EventBus.emit('Toggle:shopNavigationMenu:close');
        this.resetMenuDropDownHoverState();
        this.resetDropDownExpansion();
        this.resetDropDownState();
      }
    });

    if (this.searchInput) {
      on('blur', this.searchInput, () => Frame.EventBus.emit('Toggle:searchBarHeader:close'));
    }

    if (!this.currencySelectorDropdown) {
      return;
    }

    this.currencySelector.forEach(selector => {
      on('change', selector, () => selector.querySelector('form').submit());
    });
    on('click', document.body, event => this.handleBodyClick(event));
    on('mouseleave', this.currencySelectorDropdown, () => this.currencySelectorDropdown.classList.remove(cssClasses.active));
    const currencySelectorOption = this.currencySelectorDropdown.querySelectorAll(selectors.currencySelectorOption);
    currencySelectorOption.forEach(option => {
      on('click', option, event => this.handleCurrencyOptionClick(event));
    });
    on('click', this.currencySelectorWrap, () => this.handleCurrencySelectorClick());
  },

  /**
   * Handle custom currency option click.
   * @param {Event} event - Click event.
   *
  */
  handleCurrencyOptionClick(event) {
    const currencyCode = event.target.dataset.value;
    const existingCode = Shopify.currency.active;

    if (currencyCode === existingCode) {
      return;
    }

    const form = this.currencySelector[0].querySelector('form');
    const options = form.querySelectorAll('option');
    options.forEach(option => {
      if (option.value === currencyCode) {
        option.setAttribute('selected', 'selected');
      } else {
        option.setAttribute('selected', '');
      }
    });
    form.submit();
  },

  /**
   * Handle currency selector click.
   * @param {Event} event - Click event.
   *
  */
  handleCurrencySelectorClick() {
    if (this.currencySelectorDropdown.classList.contains(cssClasses.active)) {
      this.currencySelectorDropdown.classList.remove(cssClasses.active);
    } else {
      this.currencySelectorDropdown.classList.add(cssClasses.active);
    }
  },

  /**
   * Close currency selector on body click outside container.
   * @param {Event} event - Click event.
   *
  */
  handleBodyClick(event) {
    if (this.currencySelectorWrap.contains(event.target)) {
      return;
    }

    this.currencySelectorDropdown.classList.remove(cssClasses.active);
  },

  /**
   * Collapsing the dropdown width.
   *
   */
  resetDropDownExpansion() {
    this.siteNavDropDown.forEach(navDropDown => {
      if (navDropDown.classList.contains(cssClasses.expanded)) {
        navDropDown.classList.remove(cssClasses.expanded);
      }
    });
  },

  /**
   * Collapsing the dropdown height.
   *
   */
  resetDropDownState() {
    this.dropdownParent.forEach(dropDown => {
      if (dropDown.classList.contains(cssClasses.active)) {
        dropDown.classList.remove(cssClasses.active);
      }
    });
    this.siteNav.classList.remove(cssClasses.active);
  },

  /**
  * Keeping focused item visible while fading out sibling items on hover
  * @param {HTMLElement} element - hovered item.
  */
  highlightElement(element) {
    const siblings = getSiblings(element);
    element.classList.remove(cssClasses.disabled);
    element.classList.add(cssClasses.hovered);
    siblings.forEach(sibling => {
      sibling.classList.remove(cssClasses.hovered);
      sibling.classList.add(cssClasses.disabled);
    });
  },

  /**
  * reset sibling items fade and remove focus on hovered item
  * @param {HTMLElement} element - hovered item.
  */
  removeElementHighlight(element) {
    const siblings = getSiblings(element);
    element.classList.remove(cssClasses.hovered);
    siblings.forEach(sibling => {
      sibling.classList.remove(cssClasses.disabled);
    });
  },

  /**
  * Keeping dropdown from primary menu open
  * @param {String} handle - Handle that links between shop nav item to primary menu.
  */
  toggleNavExpand(handle) {
    const targetNavListItem = document.querySelector("[data-nav-menu-handle=\"".concat(handle, "\"]"));
    const targetNavListParentEle = targetNavListItem.parentNode;
    const primaryLink = targetNavListParentEle.closest('.site-nav__item--has-dropdown');
    const siteNav = primaryLink.closest('.site-nav');
    siteNav.classList.add(cssClasses.active);
    primaryLink.classList.add(cssClasses.active);
    targetNavListParentEle.classList.add(cssClasses.expanded);
    this.highlightElement(targetNavListItem);
  },

  /**
  * Closing and reset dropdown from primary menu
  * @param {String} handle - Handle that links between shop nav item to primary menu.
  */
  toggleNavCollapse(handle) {
    const targetNavListItem = document.querySelector("[data-nav-menu-handle=\"".concat(handle, "\"]"));
    const targetNavListParentEle = targetNavListItem.parentNode;
    const primaryLink = targetNavListParentEle.closest('.site-nav__item--has-dropdown');
    const siteNav = primaryLink.closest('.site-nav');
    siteNav.classList.remove(cssClasses.active);
    primaryLink.classList.remove(cssClasses.active);
    targetNavListParentEle.classList.remove(cssClasses.expanded);
    this.removeElementHighlight(targetNavListItem);
  },

  /**
   * Set event bus listeners based on scroll direction.
   */
  setEventBusListeners() {
    Frame.EventBus.listen('Notification:dismissed', () => {
      this.updateBodyOffset();
    });
    Frame.EventBus.listen('ScrollDirection:changed', direction => {
      if (direction === 'down') {
        this.siteHeaderWrapper.classList.add(cssClasses.collapsed);
      }
    });
    Frame.EventBus.listen('ScrollDirection:top', () => {
      this.siteHeaderWrapper.classList.remove(cssClasses.collapsed);
    });
    Frame.EventBus.listen('Toggle:siteNavDropDown:open', handle => this.toggleNavExpand(handle));
    Frame.EventBus.listen('Toggle:siteNavDropDown:close', handle => this.toggleNavCollapse(handle));
    Frame.EventBus.listen('Toggle:searchBarHeader:open', () => {
      document.documentElement.classList.add('js-search-active');
      this.searchBarHeader.setAttribute('js-search-bar-active', true);
      this.siteHeaderWrapper.classList.add(cssClasses.expanded);

      if (this.redirectModalToggle) {
        this.redirectModalToggle.classList.add(cssClasses.hidden);
      }

      if (this.searchBarToggle.length > 0) {
        [...this.searchBarToggle].forEach(button => {
          button.classList.add(cssClasses.expanded);
        });

        if (!this.container.querySelector(selectors.currencySelector)) {
          return;
        }

        this.container.querySelector(selectors.currencySelector).classList.add('search-active');
      }
    });
    Frame.EventBus.listen('Toggle:searchBarHeader:close', closeTarget => {
      /**
       * Delay state reset if target is not an overlay.
       */
      if (typeof closeTarget === 'undefined') {
        document.documentElement.classList.remove('js-search-active');
        this.searchBarHeader.setAttribute('js-search-bar-active', false);
        return;
      }

      this.siteHeaderWrapper.classList.remove(cssClasses.expanded);

      if (this.redirectModalToggle) {
        this.redirectModalToggle.classList.remove(cssClasses.hidden);
      }

      if (this.searchBarToggle.length > 0) {
        [...this.searchBarToggle].forEach(button => {
          button.classList.remove(cssClasses.expanded);
        });

        if (!this.container.querySelector(selectors.currencySelector)) {
          return;
        }

        this.container.querySelector(selectors.currencySelector).classList.add('search-active');
      }

      setTimeout(() => {
        document.documentElement.classList.remove('js-search-active');
        this.searchBarHeader.setAttribute('js-search-bar-active', false);
      }, 500);
    });
    Frame.EventBus.listen('Redirect:updateIcon', countryCode => {
      this.setStoreIcon(countryCode);
    });
    this.megaNavToggle.forEach(element => {
      on('click', element, () => this.toggleMegaNav(element));
    });
    this.navLink.forEach(element => {
      this.setKeyBlur(element);
      this.setKeyFocus(element);
    });
  }

});